'use client';

import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useScroll, useTransform, motion } from 'framer-motion';
import Image from 'next/image';
interface Image {
	src: string;
	alt?: string;
}
interface ZoomParallaxProps {
	/** Array of images to be displayed in the parallax effect max 7 images */
	images: Image[];
}

export function ZoomParallax({ images }: ZoomParallaxProps) {

  const container = useRef<HTMLDivElement | null>(null);

  const { scrollYProgress } = useScroll({
    target: container,
    offset: ['start start', 'end end'],
  });

  // ✅ Reduced scale = smoother
  const scale4 = useTransform(scrollYProgress, [0, 1], [1, 3]);
  const scale5 = useTransform(scrollYProgress, [0, 1], [1, 3.5]);
  const scale6 = useTransform(scrollYProgress, [0, 1], [1, 4]);

  const scales = useMemo(
    () => [scale4, scale5, scale6, scale5, scale6, scale5, scale4],
    [scale4, scale5, scale6]
  );

  // Use state to handle reduced motion preference
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setIsReducedMotion(mediaQuery.matches);
      
      const handleChange = (e: MediaQueryListEvent) => setIsReducedMotion(e.matches);
      mediaQuery.addEventListener('change', handleChange);
      
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  if (!mounted || isReducedMotion) {
    return null;
  }


	return (
		<div ref={container} className="relative h-[220vh] xl:block lg:hidden md:hidden hidden">
			<div className="sticky top-0 h-screen overflow-hidden">
				{images.map(({ src, alt }, index) => {
					const scale = scales[index % scales.length];

					return (
						<motion.div
							key={index}
							style={{ scale, willChange: 'transform' }}
							className={`absolute top-0 flex h-full w-full items-center justify-center ${index === 1 ? '[&>div]:!-top-[30vh] [&>div]:!left-[5vw] [&>div]:!h-[30vh] [&>div]:!w-[35vw]' : ''} ${index === 2 ? '[&>div]:!-top-[10vh] [&>div]:!-left-[25vw] [&>div]:!h-[45vh] [&>div]:!w-[20vw]' : ''} ${index === 3 ? '[&>div]:!left-[27.5vw] [&>div]:!h-[25vh] [&>div]:!w-[25vw]' : ''} ${index === 4 ? '[&>div]:!top-[27.5vh] [&>div]:!left-[5vw] [&>div]:!h-[25vh] [&>div]:!w-[20vw]' : ''} ${index === 5 ? '[&>div]:!top-[27.5vh] [&>div]:!-left-[22.5vw] [&>div]:!h-[25vh] [&>div]:!w-[30vw]' : ''} ${index === 6 ? '[&>div]:!top-[25.5vh] [&>div]:!left-[27vw] [&>div]:!h-[calc(15vh+40px)] [&>div]:!w-[calc(15vw+60px)]' : ''} `}
						>
							<div className="relative h-[25vh] w-[25vw]">
								<Image
									src={src}
									alt={alt || `Parallax image ${index + 1}`}
									fill
									quality={70}
									priority={index === 0}
									className="object-cover h-full w-full"
								/>
							</div>
						</motion.div>
					);
				})}
			</div>
		</div>
	);
}
